export interface AuthUserLike {
  id?: string | number;
  firstName?: string;
  lastName?: string;
  email?: string;
  profileImage?: string;
  avatar?: string;
  phoneNumber?: string;
  roleName?: string;
  role?: string;
  fullName?: string;
  username?: string;
  ability?: unknown;
  accessToken?: string;
  refreshToken?: string;
  [key: string]: unknown;
}

/** Build a display name from profile fields (prefers first + last name). */
export const getUserDisplayName = (
  user: AuthUserLike | null | undefined
): string => {
  if (!user) return "User";

  const fromNames = `${user.firstName || ""} ${user.lastName || ""}`.trim();

  return fromNames || user.fullName || user.username || "User";
};

export type MergedAuthUser = AuthUserLike & {
  firstName: string;
  lastName: string;
  email: string;
};

/** Merge API profile data into the stored auth user and keep derived fields in sync. */
export const mergeAuthUser = (
  currentUser: AuthUserLike | null,
  profileData: AuthUserLike
): MergedAuthUser => {
  const firstName = profileData.firstName ?? currentUser?.firstName ?? "";
  const lastName = profileData.lastName ?? currentUser?.lastName ?? "";
  const email = profileData.email ?? currentUser?.email ?? "";
  const fullName = `${firstName} ${lastName}`.trim();

  return {
    ...currentUser,
    ...profileData,
    id: profileData.id ?? currentUser?.id,
    firstName,
    lastName,
    email,
    phoneNumber: profileData.phoneNumber ?? currentUser?.phoneNumber,
    profileImage: profileData.profileImage ?? currentUser?.profileImage,
    roleName: profileData.roleName ?? currentUser?.roleName,
    role: profileData.role ?? currentUser?.role,
    ability: profileData.ability ?? currentUser?.ability,
    accessToken: currentUser?.accessToken ?? profileData.accessToken,
    refreshToken: currentUser?.refreshToken ?? profileData.refreshToken,
    fullName,
    username: fullName || profileData.username || currentUser?.username || "",
  };
};

/** Extract the user object from common API response shapes. */
export const extractProfileFromApiPayload = (
  payload: unknown
): AuthUserLike => {
  if (!payload || typeof payload !== "object") {
    return {};
  }

  const data = payload as Record<string, unknown>;

  if (data.data && typeof data.data === "object") {
    return data.data as AuthUserLike;
  }

  return data as AuthUserLike;
};
